/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { getCurrentHub, init } from '@sentry/browser';
import isError from 'lodash/isError';
import isNil from 'lodash/isNil';
import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';

import EnvUtil from '../env-util';
import ErrorLevel from '../error/error-level';
import ErrorLevelUtil from '../error/error-level-util';

/**
 * @private
 * @typedef {import('../../platform/runtime-platform').default} RuntimePlatform
 * @typedef {import('../../platform/update-platform').default} UpdatePlatform
 * @typedef {import('../env-enum').default} EnvEnum
 * @typedef {import('../logger/logger').Logger} Logger
 * @typedef {import('../url').Url} Url
 */

// TODO Chybí unit testy:
// - Původní myšlenka byla taková, že se pro testy zachová originální
//   Sentry knihovna a bude se mockovat fetch, jako výstup z této knihovny.
//   To ale bohužel není možné, protože Sentry nedovoluje opakovanou
//   inicializaci, stejně jako nepodporuje zrušení již inicializované instance.
//   Problémy s opakovanou inicializací se týkají např. nevyvolání metody
//   `setupOnce` v integracích.
//   V kombinaci naslouchání naudálosti `error` u `window` a další,
//   není vhodné tuto knihovnu inicializovat v rámci unit testů.
// - Další variantou je tedy vytvoření mocku Sentry API.
/**
 * Zaznamenávač výjimek pro Sentry API.
 * Odesílá chyby na Sentry server.
 * @implements {Logger}
 */
class SentryLogger {
  /**
   * Vytvoří instanci.
   * @param {ErrorLevel} captureErrorLevel Úroveň důležitosti chyby,
   * od které se začínají odesílat události na Sentry.
   * @param {string} dsn Sentry DSN adresa k odesílání záznamů.
   * @param {...import('@sentry/types').Integration} customIntegrations
   */
  constructor(captureErrorLevel, dsn, ...customIntegrations) {
    // TODO Sentry integrace umějí fungovat pouze s výchozím hubem.
    // - Není tedy možné používat více instancí zároveň.
    // - https://github.com/getsentry/sentry-javascript/issues/2389
    init({
      dsn,
      integrations: (sentryIntegrations) => [
        ...sentryIntegrations,
        ...customIntegrations
      ]
    });
    this.captureErrorLevel = captureErrorLevel;
    this.hub = getCurrentHub();
    // TODO #555: (Po)chybné přetypování, jelikož client může být i undefined,
    // ale všude v kódu se s tím nepočítá. Navíc se na více místech přistupuje
    // k protected property BaseClient._options.
    this.client =
      /**
       * @type {import('@sentry/browser').BrowserClient & {
       *   _options: import('@sentry/browser').BrowserOptions,
       * }}
       */
      (this.hub.getClient());
  }

  /**
   * Zaznamená libovolnou hodnotu,
   * přičemž se hodnota úrovně chyby použije primárně z této hodnoty
   * a sekundárně z názvu metody zanamenávače chyb,
   * přes kterou byla chyba předána.
   * @param {ErrorLevel} methodLevel Úroveň chyby, která přísluší metodě,
   * která byla na zaznamenávači výjimek volána.
   * @param {any} value
   * @private
   */
  capture(methodLevel, value) {
    const valueLevel = ErrorLevelUtil.fromValue(value);
    const level = valueLevel ?? methodLevel;
    if (isError(value)) {
      return this.captureError(level, value);
    }
    if (isObjectLike(value)) {
      return this.captureObject(level, value);
    }
    return this.captureString(level, String(value));
  }

  /**
   * Zaznamená chybový objekt s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {Error} error
   * @private
   */
  captureError(level, error) {
    const { hub } = this;
    const severity = fromErrorLevel(level);
    if (this.isBreadcrumb(level)) {
      hub.addBreadcrumb({
        message: error.toString(),
        level: severity
      });
      return;
    }
    hub.withScope((scope) => {
      scope.setLevel(severity);
      hub.captureException(error);
    });
  }

  /**
   * Zaznamená obecný objekt s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {Object} object
   * @private
   */
  captureObject(level, object) {
    const { message, name } = object;
    if (!isString(message)) {
      return this.captureString(level, String(object));
    }
    if (!isString(name)) {
      return this.captureString(level, message);
    }
    return this.captureString(level, `${name}: ${message}`);
  }

  /**
   * Zaznamená řetězec s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {string} string
   * @private
   */
  captureString(level, string) {
    const { hub } = this;
    const severity = fromErrorLevel(level);
    if (this.isBreadcrumb(level)) {
      hub.addBreadcrumb({ message: string, level: severity });
      return;
    }
    hub.captureMessage(string, severity);
  }

  /** @type {Logger['debug']} */
  debug(value) {
    return this.capture(ErrorLevel.Debug, value);
  }

  /** @type {Logger['error']} */
  error(value) {
    return this.capture(ErrorLevel.Error, value);
  }

  /** @type {Logger['info']} */
  info(value) {
    return this.capture(ErrorLevel.Info, value);
  }

  /**
   * Určí, jestli se má daná úroveň důležitosti zalogovat jako Sentry
   * breadcrumb, která se samostatně neodesílá na server, nebo jako plnohodnotná
   * chyba, která se ihned odešle na server i s předchozími breadcrumb.
   * @param {ErrorLevel} level
   * @private
   * @returns {boolean}
   */
  isBreadcrumb(level) {
    return ErrorLevelUtil.compare(level, this.captureErrorLevel) < 0;
  }

  /** @type {Logger['log']} */
  log(value) {
    return this.capture(ErrorLevel.Log, value);
  }

  /**
   * @param {string | undefined} buildId
   * @private
   */
  setBuildId(buildId) {
    if (isNil(buildId)) {
      return;
    }
    this.hub.setExtra('buildId', buildId);
  }

  /**
   * @param {string | undefined} deviceId
   * @private
   */
  setDeviceId(deviceId) {
    if (isNil(deviceId)) {
      return;
    }
    this.hub.setUser({ id: deviceId });
  }

  /**
   * @param {EnvEnum | undefined} envEnum
   * @private
   */
  setEnvEnum(envEnum) {
    if (isNil(envEnum)) {
      return;
    }
    // TODO Změna environment po inicializaci není oficiálně podporována.
    // - https://github.com/getsentry/sentry-javascript/issues/2388
    // eslint-disable-next-line no-underscore-dangle
    this.client._options.environment = EnvUtil.generate(envEnum);
  }

  /** @type {Logger['setInfo']} */
  setInfo(loggerInfo) {
    this.setBuildId(loggerInfo.buildId);
    this.setDeviceId(loggerInfo.deviceId);
    this.setEnvEnum(loggerInfo.envEnum);
    this.setManufacturer(loggerInfo.manufacturer);
    this.setModelName(loggerInfo.modelName);
    this.setOnline(loggerInfo.online);
    this.setRuntimePlatform(loggerInfo.runtimePlatform);
    this.setServerUrl(loggerInfo.serverUrl);
    this.setUpdatePlatform(loggerInfo.updatePlatform);
    this.setUpdateUrl(loggerInfo.updateUrl);
    this.setVersion(loggerInfo.version);
  }

  /**
   * @param {string | undefined} manufacturer
   * @private
   */
  setManufacturer(manufacturer) {
    if (isNil(manufacturer)) {
      return;
    }
    this.hub.setExtra('manufacturer', manufacturer);
  }

  /**
   * @param {string | undefined} modelName
   * @private
   */
  setModelName(modelName) {
    if (isNil(modelName)) {
      return;
    }
    this.hub.setExtra('modelName', modelName);
  }

  /**
   * @param {boolean | undefined} online
   * @private
   */
  setOnline(online) {
    if (isNil(online)) {
      return;
    }
    this.hub.setExtra('online', online);
  }

  /**
   * @param {RuntimePlatform | undefined} runtimePlatform
   * @private
   */
  setRuntimePlatform(runtimePlatform) {
    if (isNil(runtimePlatform)) {
      return;
    }
    this.hub.setExtra('runtimePlatform', runtimePlatform);
  }

  /**
   * @param {string | undefined} serverUrl
   * @private
   */
  setServerUrl(serverUrl) {
    if (isNil(serverUrl)) {
      return;
    }
    this.hub.setExtra('serverUrl', serverUrl);
  }

  /**
   * @param {UpdatePlatform | undefined} updatePlatform
   * @private
   */
  setUpdatePlatform(updatePlatform) {
    if (isNil(updatePlatform)) {
      return;
    }
    this.hub.setExtra('updatePlatform', updatePlatform);
  }

  /**
   * @param {Url | undefined} updateUrl
   * @private
   */
  setUpdateUrl(updateUrl) {
    if (isNil(updateUrl)) {
      return;
    }
    this.hub.setExtra('updateUrl', updateUrl);
  }

  /**
   * @param {string | undefined} version
   * @private
   */
  setVersion(version) {
    if (isNil(version)) {
      return;
    }
    // TODO Změna release po inicializaci není oficiálně podporována.
    // - https://github.com/getsentry/sentry-javascript/issues/2387
    // eslint-disable-next-line no-underscore-dangle
    this.client._options.release = version;
  }

  /** @type {Logger['warn']} */
  warn(value) {
    return this.capture(ErrorLevel.Warning, value);
  }
}

export default SentryLogger;

/**
 * Vytvoří Sentry úroveň chyby z obecného číselníku chyb.
 * @param {ErrorLevel} errorLevel
 * @returns {import('@sentry/types').SeverityLevel}
 */
function fromErrorLevel(errorLevel) {
  switch (errorLevel) {
    case ErrorLevel.Debug:
      return 'debug';
    case ErrorLevel.Error:
      return 'error';
    case ErrorLevel.Info:
      return 'info';
    case ErrorLevel.Log:
      return 'log';
    case ErrorLevel.Warning:
      return 'warning';
    default:
      throw new RangeError(`Unknown error level "${errorLevel}".`);
  }
}
